<template>
  <v-row class="pt-2">
    <v-col>
      <h2>Client notes</h2>
      <div class="text-center" v-if="loading">
        <v-progress-circular
          :indeterminate="loading"
          color="blue"
          :size="50"
        ></v-progress-circular>
      </div>
      <v-alert v-if="notes.length == 0" class="mt-5" type="info">
        No client notes
      </v-alert>
      <v-card
        v-for="(note) in notes"
        :key="note.id"
        small
        fill-dot
        class="ma-3"
        elevation="4"
      >
        <v-chip :color="note.importance | color" label class="full-width">
          <v-card-title class="pl-2">
            <v-icon left> {{ note.importance | icon }} </v-icon>
            <div v-if="!note.hubspot && note.assessment.number != null">
              Note taken in 
              <span
                v-if="note.assessment.number == 0"
                class="font-weight-bold"
                >Initial check-in</span
              >
              <span v-else class="font-weight-bold"
                >Check-in {{ note.assessment.number }}
              </span>

              <span v-if="note.assessment.date">
                ({{ note.assessment.date | fancyShortDate }})</span
              >
            </div>
            <div
              v-if="note.title"
              class="font-weight-bold"
              style="font-size: 20px"
            >
              {{ note.title }}<br />
            </div>
          </v-card-title>
        </v-chip>
        <v-card-subtitle class="py-1">
          <div class="blue-grey--text text--lighten-2 text-right">
            {{ note.createdAt | fancyDate }}
          </div>
        </v-card-subtitle>

        <v-card-text v-if="note.hubspot" v-html="note.note"> </v-card-text>
        <v-card-text v-else class="pb-0 mb-0">
          <span class="authentic-note">{{ note.note }}</span>
        </v-card-text>


        <v-card-actions>
          <div class="ml-n2">
            <v-card-title class="d-inline-flex pb-3">
              <div class="blue-grey--text text--lighten-2 ml-auto">
                <span>{{ note.coach }}</span>
              </div>
            </v-card-title>
            <v-card-subtitle class="blue-grey--text text--lighten-2">
              <span v-if="note.hubspot">via Hubspot</span>
              <span v-else>via Coaching Platform</span>
            </v-card-subtitle>
          </div>

          <div v-if="!note.hubspot && note.assessment.id" class="ml-auto mt-auto">
            <v-spacer></v-spacer>
            <v-btn :to="assessmentLink(note.assessment.id)" target="_blank" class="ma-2"
              >View Check-in</v-btn
            >
          </div>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import {
  USER_NOTES_QUERY,
  HUBSPOT_NOTES_QUERY,
} from "@/constants/graphql/coach/notes";
import { fancy_date } from "@/lib/fancy_date";
import moment from "moment";

export default {
  name: "UserNotes",
  props: {
    userId: {
      type: String,
      default: null,
    }
  },
  data() {
    return {
      loading: true,
      userNotes: null,
      hubspotNotes: null,
      notes: [],
      problemConnecting: false,
      form: {
        importance: [
          { text: "Note", value: "Info" },
          { text: "Warning", value: "Warning" },
          { text: "Urgent", value: "Urgent" },
        ],
      },
    };
  },
  filters: {
    fancyDate(date) {
      return fancy_date(date);
    },
    fancyShortDate(date) {
      return fancy_date(date, "D MMMM YYYY", false);
    },
    color(importance) {
      let map = {
        info: "info",
        warning: "warning",
        urgent: "error",
        assessment: "primary",
      };

      return importance ? map[importance.toLowerCase()] : "secondary";
    },
    icon(importance) {
      let map = {
        info: "mdi-notebook",
        warning: "mdi-alert-circle",
        urgent: "mdi-alert-decagram",
        assessment: "mdi-calendar-text",
      };

      return importance ? map[importance.toLowerCase()] : "mdi-folder";
    },
  },
  methods: {
    assessmentLink(id) {
      return { 
        name: 'CoachClientAssessments', 
        params: 
          { coachId: this.coachId, 
            userId: this.program.user.id, 
          },
        query: {
            assessmentId: id 
          }
        };
    },
    setNotes() {
      if (this.userNotes != null && this.hubspotNotes != null){
        let notes = this.userNotes;
        notes = notes.concat(this.hubspotNotes)

        if (notes.length > 0) {
          notes.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
          this.notes = notes;
        }

        this.loading = false;
      }
    },
  },
  apollo: {
    userNotes: {
      query: USER_NOTES_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.userNotes = [];

          response.data.userNotes.forEach((note) => {
            this.userNotes.push({
              id: note.id,
              hubspot: false,
              coach: `${note.coach.firstName} ${note.coach.lastName}`,
              importance: note.importance,
              note: note.note,
              title: note.title,
              assessment: {
                id: note.assessment ? note.assessment.id : null,
                number: note.assessment ? note.assessment.number : null,
                date: note.assessment?.requiredDate
                  ? note.assessment.requiredDate
                  : null,
              },
              createdAt: moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
            });
          });

          this.setNotes();
        }
      },
    },
    hubspotNotes: {
      query: HUBSPOT_NOTES_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.hubspotNotes = [];
          response.data.hubspotNotes.forEach((note) => {
            this.hubspotNotes.push({
              id: null,
              hubspot: true,
              coach: note.source,
              importance: null,
              note: note.body,
              title: null,
              assessment: null,
              createdAt: moment(note.createdAt, "YYYY-MM-DD HH:mm:ss"),
            });
          });

          this.setNotes();
        }
      },
    },
  },
};
</script>

<style scoped>
.more-limited-height {
  height: 90%;
}
.limited-height {
  height: 100%;
}
.overflow {
  overflow: auto;
}
.authentic-note {
  white-space: pre-line;
  font-size: 16px;
  color: black;
}
.full-width {
  width: 100%;
}
</style>